import { BackgroundOption, BlurStrength, PRESET_BACKGROUNDS } from '../../hooks/useVirtualBackground';

interface BackgroundSettingsProps {
  currentBackground: BackgroundOption;
  isApplying: boolean;
  isSupported: boolean;
  error: string | null;
  onApplyBackground: (option: BackgroundOption) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

const BLUR_OPTIONS: { value: BlurStrength; label: string }[] = [
  { value: 'low', label: 'Light' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'Strong' },
];

export function BackgroundSettings({
  currentBackground,
  isApplying,
  isSupported,
  error,
  onApplyBackground,
  isOpen,
  onClose,
}: BackgroundSettingsProps) {
  if (!isOpen) return null;

  const handleSelectNone = () => {
    onApplyBackground({ type: 'none' });
  };

  const handleSelectBlur = (strength: BlurStrength) => {
    onApplyBackground({ type: 'blur', blurStrength: strength });
  };

  const handleSelectImage = (imageUrl: string, imageName: string) => {
    onApplyBackground({ type: 'image', imageUrl, imageName });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Background Settings</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {!isSupported ? (
            <div className="text-center py-4">
              <div className="text-yellow-500 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-gray-600">
                Virtual backgrounds are not supported on this device or browser.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Try using Chrome, Edge, or Firefox on a modern device.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* None Option */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">No Effect</h4>
                <button
                  onClick={handleSelectNone}
                  disabled={isApplying}
                  className={`w-full p-3 rounded-lg border-2 transition-colors ${
                    currentBackground.type === 'none'
                      ? 'border-aalaw-red bg-aalaw-red/5'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isApplying ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-900">None</span>
                  </div>
                </button>
              </div>

              {/* Blur Options */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Background Blur</h4>
                <div className="grid grid-cols-3 gap-2">
                  {BLUR_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => handleSelectBlur(value)}
                      disabled={isApplying}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        currentBackground.type === 'blur' && currentBackground.blurStrength === value
                          ? 'border-aalaw-red bg-aalaw-red/5'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isApplying ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="w-full aspect-video bg-gradient-to-br from-gray-200 to-gray-300 rounded mb-2 flex items-center justify-center">
                        <div
                          className="w-8 h-8 bg-gray-400 rounded-full"
                          style={{
                            filter: `blur(${value === 'low' ? 2 : value === 'medium' ? 4 : 6}px)`,
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Background Images */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Background Images</h4>
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_BACKGROUNDS.map((bg) => (
                    <button
                      key={bg.id}
                      onClick={() => handleSelectImage(bg.url, bg.name)}
                      disabled={isApplying}
                      className={`p-2 rounded-lg border-2 transition-colors ${
                        currentBackground.type === 'image' && currentBackground.imageUrl === bg.url
                          ? 'border-aalaw-red bg-aalaw-red/5'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isApplying ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="w-full aspect-video bg-gray-100 rounded mb-2 overflow-hidden">
                        <img
                          src={bg.url}
                          alt={bg.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e5e7eb" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="12">No Image</text></svg>';
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-700">{bg.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Loading indicator */}
              {isApplying && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-aalaw-red"></div>
                  Applying background...
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
